import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["body", "data", "list", "status"]
  static values = { url: String, avatarFallback: String, loading: String, empty: String, error: String }

  connect() {
    this.initialData ??= this.dataTarget.value
    this.previousBody = this.bodyTarget.value
    try {
      const data = JSON.parse(this.dataTarget.value || "[]")
      this.mentions = Array.isArray(data) ? data.filter(mention =>
        mention && Number.isInteger(mention.user_id) && mention.user_id > 0 &&
        Number.isInteger(mention.start) && mention.start >= 0 &&
        Number.isInteger(mention.length) && mention.length > 0 &&
        typeof mention.text === "string" && Array.from(mention.text).length === mention.length &&
        Array.from(this.previousBody).slice(mention.start, mention.start + mention.length).join("") === mention.text
      ) : []
    } catch (_) {
      this.mentions = []
    }
    this.serialize()
    this.requestId = this.requestId || 0
    this.composing = false
    this.close()
  }

  disconnect() {
    this.close()
  }

  input(event) {
    this.trackChanges()
    if (!this.composing && !event?.isComposing) this.schedule()
  }

  trackChanges() {
    const before = Array.from(this.previousBody)
    const after = Array.from(this.bodyTarget.value)
    if (this.previousBody === this.bodyTarget.value) return
    let start = 0
    while (start < before.length && start < after.length && before[start] === after[start]) start++
    let end = before.length
    let newEnd = after.length
    while (end > start && newEnd > start && before[end - 1] === after[newEnd - 1]) { end--; newEnd-- }
    const delta = newEnd - end
    this.mentions = this.mentions.flatMap(mention => {
      const mentionEnd = mention.start + mention.length
      if (end <= mention.start) return [{ ...mention, start: mention.start + delta }]
      if (start >= mentionEnd) return [mention]
      return []
    }).filter(mention => after.slice(mention.start, mention.start + mention.length).join("") === mention.text)
    this.previousBody = this.bodyTarget.value
    this.serialize()
  }

  serialize() {
    this.dataTarget.value = JSON.stringify(this.mentions)
  }

  query() {
    if (this.bodyTarget.selectionStart !== this.bodyTarget.selectionEnd) return null
    const prefix = this.bodyTarget.value.slice(0, this.bodyTarget.selectionStart)
    const match = prefix.match(/(?:^|[\s([{])@([^@\r\n]+)$/u)
    if (!match || Array.from(match[1].trim()).length < 2 || Array.from(match[1]).length > 100) return null
    const end = Array.from(prefix).length
    const start = end - Array.from(match[1]).length - 1
    if (this.mentions.some(mention => start >= mention.start && start < mention.start + mention.length)) return null
    return { start, end, name: match[1].trim() }
  }

  schedule() {
    this.close()
    const query = this.query()
    if (!query || this.composing) return
    this.timer = setTimeout(() => this.search(query), 200)
  }

  async search(query) {
    this.abortController = new AbortController()
    const requestId = ++this.requestId
    this.statusTarget.textContent = this.loadingValue
    this.listTarget.setAttribute("aria-busy", "true")
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query.name)
      const response = await fetch(url, { headers: { Accept: "application/json" }, signal: this.abortController.signal })
      if (!response.ok || response.redirected) throw new Error("Mention search failed")
      const candidates = await response.json()
      if (requestId !== this.requestId || JSON.stringify(query) !== JSON.stringify(this.query())) return
      if (!Array.isArray(candidates)) throw new Error("Invalid candidates")
      this.activeQuery = query
      this.candidates = candidates.slice(0, 10)
      this.renderCandidates()
    } catch (error) {
      if (requestId === this.requestId && error.name !== "AbortError") this.statusTarget.textContent = this.errorValue
    } finally {
      if (requestId === this.requestId) this.listTarget.removeAttribute("aria-busy")
    }
  }

  renderCandidates() {
    this.listTarget.replaceChildren()
    this.candidates.forEach((candidate, index) => {
      const option = document.createElement("div")
      option.id = `${this.listTarget.id}-${index}`
      option.className = "community-mention-option d-flex align-items-center gap-2 px-3 py-2"
      option.setAttribute("role", "option")
      option.tabIndex = -1
      option.dataset.index = index
      option.dataset.action = "pointerdown->community-mentions#keepFocus pointercancel->community-mentions#close click->community-mentions#select"
      const avatar = document.createElement("img")
      avatar.src = candidate.avatar_url || this.avatarFallbackValue
      avatar.alt = ""
      avatar.className = "community-mention-avatar rounded-circle flex-shrink-0"
      avatar.dataset.controller = "image-fallback"
      avatar.dataset.imageFallbackSrcValue = this.avatarFallbackValue
      avatar.dataset.action = "error->image-fallback#replace"
      option.append(avatar)
      const name = document.createElement("span")
      name.textContent = candidate.name
      option.append(name)
      this.listTarget.append(option)
    })
    this.statusTarget.textContent = this.candidates.length ? "" : this.emptyValue
    this.listTarget.hidden = !this.candidates.length
    this.bodyTarget.setAttribute("aria-expanded", String(!!this.candidates.length))
    this.highlight(0)
  }

  highlight(index) {
    this.activeIndex = index
    Array.from(this.listTarget.children).forEach((option, optionIndex) => option.setAttribute("aria-selected", String(optionIndex === index)))
    const option = this.listTarget.children[index]
    if (option) {
      this.bodyTarget.setAttribute("aria-activedescendant", option.id)
      option.scrollIntoView?.({ block: "nearest" })
    }
  }

  keydown(event) {
    if (event.isComposing || this.composing || event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key === "Escape") {
      if (!this.listTarget.hidden || this.statusTarget.textContent) {
        event.preventDefault()
        event.stopPropagation()
      }
      this.close()
      return
    }
    if (this.listTarget.hidden || !this.candidates?.length) return
    if (JSON.stringify(this.activeQuery) !== JSON.stringify(this.query())) {
      this.close()
      return
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      this.highlight((this.activeIndex + step + this.candidates.length) % this.candidates.length)
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.insert(this.candidates[this.activeIndex])
    }
  }

  keepFocus(event) {
    this.picking = true
    // Retain the caret on mouse selection without blocking touch scrolling.
    if (event.pointerType === "mouse") event.preventDefault()
  }

  select(event) {
    event.preventDefault()
    this.insert(this.candidates[Number(event.currentTarget.dataset.index)])
  }

  insert(candidate) {
    const query = this.activeQuery
    if (!candidate || !query || JSON.stringify(query) !== JSON.stringify(this.query())) return
    const text = `@${candidate.name}`
    const body = Array.from(this.bodyTarget.value)
    this.bodyTarget.value = body.slice(0, query.start).join("") + text + " " + body.slice(query.end).join("")
    this.trackChanges()
    this.mentions.push({ user_id: candidate.id, start: query.start, length: Array.from(text).length, text })
    this.serialize()
    const caret = body.slice(0, query.start).join("").length + text.length + 1
    this.bodyTarget.focus({ preventScroll: true })
    this.bodyTarget.setSelectionRange(caret, caret)
    this.bodyTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.close()
  }

  compositionStart() {
    this.composing = true
    this.close()
  }

  compositionEnd() {
    this.composing = false
    this.input()
  }

  blur(event) {
    if (!this.picking && !this.element.contains(event.relatedTarget)) this.close()
  }

  reset(event) {
    if (event.target !== this.element.closest("form")) return
    queueMicrotask(() => {
      if (this.element.isConnected) {
        this.dataTarget.value = this.initialData
        this.connect()
      }
    })
  }

  close() {
    this.picking = false
    clearTimeout(this.timer)
    this.abortController?.abort()
    this.requestId++
    this.candidates = []
    this.activeQuery = null
    this.listTarget.hidden = true
    this.listTarget.replaceChildren()
    this.listTarget.removeAttribute("aria-busy")
    this.statusTarget.textContent = ""
    this.bodyTarget.setAttribute("aria-expanded", "false")
    this.bodyTarget.removeAttribute("aria-activedescendant")
  }
}
